/**
 * Strict allowlisted rebuild for trace evaluation GET/PUT payloads.
 */

import {
  COGNITORY_ACTIVITY_EVALUATION_RATINGS,
  type CognitoryActivityEvaluationRating,
} from "../presentation/cognitoryActivityEvaluationStatus";

export type CognitoryTraceEvaluationView = {
  rating: CognitoryActivityEvaluationRating;
  comment?: string;
  createdAt: string;
  updatedAt: string;
};

const RATING_SET = new Set<string>(COGNITORY_ACTIVITY_EVALUATION_RATINGS);
const V2_ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const REQUEST_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/;
const FORBIDDEN_TREE_KEYS = new Set<string>(["canonicalUserId"]);

function isValidRequestId(value: unknown): value is string {
  return typeof value === "string" && REQUEST_ID_PATTERN.test(value);
}

function isValidCognitoryId(value: unknown): boolean {
  return typeof value === "string" && V2_ID_PATTERN.test(value);
}

function isValidTraceRequestId(value: unknown): boolean {
  return typeof value === "string" && V2_ID_PATTERN.test(value);
}

function isIsoZ(value: string): boolean {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return false;
  }
  return new Date(parsed).toISOString() === value;
}

export function treeContainsForbiddenIdentityKey(value: unknown): boolean {
  if (value === null || typeof value !== "object") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some(item => treeContainsForbiddenIdentityKey(item));
  }
  for (const [key, nested] of Object.entries(
    value as Record<string, unknown>,
  )) {
    if (FORBIDDEN_TREE_KEYS.has(key)) {
      return true;
    }
    if (treeContainsForbiddenIdentityKey(nested)) {
      return true;
    }
  }
  return false;
}

export type CognitoryTraceEvaluationGetRebuildResult =
  | {
      kind: "found";
      supportReference: string;
      cognitoryId: string;
      traceRequestId: string;
      evaluation: CognitoryTraceEvaluationView;
    }
  | {
      kind: "no_evaluation";
      supportReference: string;
      cognitoryId: string;
      traceRequestId: string;
    }
  | { kind: "malformed_success"; supportReference: string | null };

export function rebuildCognitoryTraceEvaluationGetPayload(input: {
  expectedCognitoryId: string;
  expectedTraceRequestId: string;
  body: unknown;
}): CognitoryTraceEvaluationGetRebuildResult {
  if (treeContainsForbiddenIdentityKey(input.body)) {
    return { kind: "malformed_success", supportReference: null };
  }
  if (!input.body || typeof input.body !== "object") {
    return { kind: "malformed_success", supportReference: null };
  }
  const record = input.body as Record<string, unknown>;
  if (!isValidRequestId(record.requestId)) {
    return { kind: "malformed_success", supportReference: null };
  }
  const requestId = record.requestId as string;
  if (
    !isValidCognitoryId(record.cognitoryId) ||
    record.cognitoryId !== input.expectedCognitoryId
  ) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  if (
    !isValidTraceRequestId(record.traceRequestId) ||
    record.traceRequestId !== input.expectedTraceRequestId
  ) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  if (record.decision === "no_evaluation") {
    const keys = Object.keys(record).sort();
    if (keys.join(",") !== "cognitoryId,decision,requestId,traceRequestId") {
      return { kind: "malformed_success", supportReference: requestId };
    }
    return {
      kind: "no_evaluation",
      supportReference: requestId,
      cognitoryId: record.cognitoryId as string,
      traceRequestId: record.traceRequestId as string,
    };
  }
  if (record.decision !== "evaluation_found") {
    return { kind: "malformed_success", supportReference: requestId };
  }
  const rating = String(record.rating ?? "");
  if (!RATING_SET.has(rating)) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  const createdAt = String(record.createdAt ?? "");
  const updatedAt = String(record.updatedAt ?? "");
  if (!isIsoZ(createdAt) || !isIsoZ(updatedAt)) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  let comment: string | undefined;
  const keys = Object.keys(record).sort();
  const baseKeys = [
    "cognitoryId",
    "createdAt",
    "decision",
    "rating",
    "requestId",
    "traceRequestId",
    "updatedAt",
  ];
  if (keys.join(",") === [...baseKeys, "comment"].sort().join(",")) {
    if (typeof record.comment !== "string") {
      return { kind: "malformed_success", supportReference: requestId };
    }
    const trimmed = record.comment.trim();
    if (trimmed.length > 0) {
      comment = trimmed;
    }
  } else if (keys.join(",") !== baseKeys.sort().join(",")) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  return {
    kind: "found",
    supportReference: requestId,
    cognitoryId: record.cognitoryId as string,
    traceRequestId: record.traceRequestId as string,
    evaluation: {
      rating: rating as CognitoryActivityEvaluationRating,
      createdAt,
      updatedAt,
      ...(comment !== undefined ? { comment } : {}),
    },
  };
}

export type CognitoryTraceEvaluationPutRebuildResult =
  | {
      kind: "recorded";
      supportReference: string;
      cognitoryId: string;
      traceRequestId: string;
      rating: CognitoryActivityEvaluationRating;
      updatedAt: string;
      comment?: string;
    }
  | { kind: "malformed_success"; supportReference: string | null };

export function rebuildCognitoryTraceEvaluationPutPayload(input: {
  expectedCognitoryId: string;
  expectedTraceRequestId: string;
  body: unknown;
}): CognitoryTraceEvaluationPutRebuildResult {
  if (treeContainsForbiddenIdentityKey(input.body)) {
    return { kind: "malformed_success", supportReference: null };
  }
  if (!input.body || typeof input.body !== "object") {
    return { kind: "malformed_success", supportReference: null };
  }
  const record = input.body as Record<string, unknown>;
  if (!isValidRequestId(record.requestId)) {
    return { kind: "malformed_success", supportReference: null };
  }
  const requestId = record.requestId as string;
  if (record.decision !== "evaluation_recorded") {
    return { kind: "malformed_success", supportReference: requestId };
  }
  if (
    !isValidCognitoryId(record.cognitoryId) ||
    record.cognitoryId !== input.expectedCognitoryId
  ) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  if (
    !isValidTraceRequestId(record.traceRequestId) ||
    record.traceRequestId !== input.expectedTraceRequestId
  ) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  const rating = String(record.rating ?? "");
  if (!RATING_SET.has(rating)) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  const updatedAt = String(record.updatedAt ?? "");
  if (!isIsoZ(updatedAt)) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  const keys = Object.keys(record).sort();
  const baseKeys = [
    "cognitoryId",
    "decision",
    "rating",
    "requestId",
    "traceRequestId",
    "updatedAt",
  ];
  let comment: string | undefined;
  if (keys.join(",") === [...baseKeys, "comment"].sort().join(",")) {
    if (typeof record.comment !== "string") {
      return { kind: "malformed_success", supportReference: requestId };
    }
    const trimmed = record.comment.trim();
    if (trimmed.length > 0) {
      comment = trimmed;
    }
  } else if (keys.join(",") !== baseKeys.sort().join(",")) {
    return { kind: "malformed_success", supportReference: requestId };
  }
  return {
    kind: "recorded",
    supportReference: requestId,
    cognitoryId: record.cognitoryId as string,
    traceRequestId: record.traceRequestId as string,
    rating: rating as CognitoryActivityEvaluationRating,
    updatedAt,
    ...(comment !== undefined ? { comment } : {}),
  };
}
